import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import { INTL_LOCALE } from '@mrigl/i18n';
import { LOCALES, type Locale } from '@mrigl/shared';
import {
  Alert,
  Button,
  Card,
  CheckboxGroup,
  SelectField,
  TextAreaField,
  TextField,
} from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../api.js';
import { Loading } from '../components.js';
import {
  GridBody,
  GridToolbar,
  Pagination,
  SearchFilter,
  SortHeader,
  useGridState,
  usePagedData,
  useRowLink,
  type ListQuery,
} from '../grid.js';
import { useI18n, type I18n } from '../i18n.js';
import { planName, statusText } from '../pages/billing/BillingPage.js';
import { dinarsToMinor, minorToDinars } from '../pages/shipping/CarriersPage.js';
import { OperatorStoreMerchantCard } from './OperatorMerchantsPage.js';

type Plan = Schemas['Plan'];
type PlanListItem = Schemas['PlanList']['items'][number];
type PlanDetail = Schemas['PlanDetail'];
type StoreRow = Schemas['OperatorStore'];
type StoresQuery = ListQuery<'/operator-api/v1/stores'>;
type StoreSort = NonNullable<StoresQuery['sort']>;
type StoreStateFilter = NonNullable<StoresQuery['state']>;
type StoreDetail = Schemas['OperatorStoreDetail'];
type SettingsState = Schemas['PlatformBillingSettingsState'];
type SubState = StoreDetail['status']['state'];

function operatorError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError) {
    if (failure.code === 'invoice-settings-missing') return t('admin.operator.error.settings');
    if (failure.code === 'invalid-transition') return t('admin.operator.error.noPlan');
    if (failure.code === 'validation-failed' || failure.code === 'conflict') {
      return t('admin.operator.plan.error');
    }
  }
  return errorMessage(t, failure);
}

const optionalInt = (value: string) => (value.trim() === '' ? null : Number(value));

function formatDay(locale: Locale, value: string): string {
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], {
    dateStyle: 'medium',
    timeZone: 'Africa/Tunis',
  }).format(new Date(value));
}

type Payment = StoreDetail['payments'][number];
type Invoice = StoreDetail['invoices'][number];

function paymentLedger(
  payments: readonly Payment[],
  invoices: readonly Invoice[],
): { payment: Payment | null; invoice: Invoice | null }[] {
  const remaining = [...invoices];
  const rows = payments.map((payment) => {
    const index = remaining.findIndex(
      (invoice) =>
        invoice.periodFrom === payment.periodStart && invoice.periodTo === payment.periodEnd,
    );
    return {
      payment,
      invoice: index === -1 ? null : (remaining.splice(index, 1)[0] ?? null),
    };
  });
  return [...rows, ...remaining.map((invoice) => ({ payment: null, invoice }))];
}

function localizedName(
  name: Partial<Record<string, string>>,
  locale: Locale,
  fallback: string,
): string {
  return name[locale] ?? Object.values(name)[0] ?? fallback;
}

function SubscriptionBadge({ state }: { readonly state: SubState }) {
  const { t } = useI18n();
  return (
    <span className={`admin-status admin-status--sub-${state}`}>
      {t(`admin.operator.stateShort.${state}`)}
    </span>
  );
}

const STORE_STATES: readonly StoreStateFilter[] = [
  'none',
  'free',
  'trialing',
  'active',
  'grace',
  'restricted',
];

function storeDeadline(store: StoreRow): string | null {
  const { state, trialEndsAt, graceEndsAt } = store.subscription;
  if (state === 'trialing') return trialEndsAt;
  if (state === 'grace' || state === 'restricted') return graceEndsAt;
  return null;
}

/** Operator console home: stores with their plan, payment and subscription dates. */
export function OperatorStoresPage() {
  const { t, locale, formatPrice } = useI18n();
  const rowLink = useRowLink();
  const [plans, setPlans] = useState<PlanListItem[]>([]);
  const grid = useGridState({
    sort: '-createdAt',
    sortable: ['createdAt', 'slug', 'planCode', 'paidUntil', 'state'],
    filters: { q: '', plan: '', state: '' },
  });
  const { filters } = grid;
  const key = JSON.stringify([grid.page, grid.pageSize, grid.sort, filters]);
  const { data, loading, error } = usePagedData<StoreRow>(
    key,
    () =>
      unwrap(
        api.GET('/operator-api/v1/stores', {
          params: {
            query: {
              page: grid.page,
              pageSize: grid.pageSize,
              sort: grid.sort as StoreSort,
              ...(filters.q === '' ? {} : { q: filters.q }),
              ...(filters.plan === '' ? {} : { plan: filters.plan }),
              ...(filters.state === '' ? {} : { state: filters.state as StoreStateFilter }),
            },
          },
        }),
      ),
    grid.setPage,
  );

  useEffect(() => {
    unwrap(api.GET('/operator-api/v1/plans'))
      .then((list) => {
        setPlans(list.items);
      })
      .catch(() => {
        setPlans([]);
      });
  }, []);

  return (
    <div className="mrigl-stack">
      <div className="admin-pagehead">
        <h1 className="mrigl-heading">{t('admin.operator.stores')}</h1>
      </div>
      <p className="mrigl-muted">{t('admin.operator.stores.intro')}</p>
      <GridToolbar
        activeFilters={grid.activeFilters}
        onReset={grid.reset}
        search={
          <SearchFilter
            label={t('admin.operator.search')}
            value={filters.q}
            onChange={(value) => {
              grid.setFilter('q', value);
            }}
          />
        }
      >
        <SelectField
          label={t('admin.operator.planFilter')}
          value={filters.plan}
          onChange={(event) => {
            grid.setFilter('plan', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.operator.plan.any') },
            ...plans.map((plan) => ({ value: plan.code, label: plan.code })),
          ]}
        />
        <SelectField
          label={t('admin.operator.stateFilter')}
          value={filters.state}
          onChange={(event) => {
            grid.setFilter('state', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.operator.state.any') },
            ...STORE_STATES.map((state) => ({
              value: state,
              label: t(`admin.operator.stateShort.${state}`),
            })),
          ]}
        />
      </GridToolbar>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {data === null ? (
        loading ? (
          <Loading />
        ) : null
      ) : (
        <>
          <GridBody
            loading={loading}
            empty={data.items.length === 0}
            filtered={grid.activeFilters > 0}
            emptyText={t('admin.operator.noStores')}
          >
            <table className="mrigl-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.operator.feedback.store')}</th>
                  <SortHeader
                    field="slug"
                    label={t('admin.store.slug')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                  />
                  <SortHeader
                    field="planCode"
                    label={t('admin.operator.planFilter')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                  />
                  <th scope="col">{t('admin.operator.lastPayment')}</th>
                  <SortHeader
                    field="paidUntil"
                    label={t('admin.operator.renew')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                    firstDescending
                  />
                  <th scope="col">{t('admin.operator.deadline')}</th>
                  <SortHeader
                    field="state"
                    label={t('admin.order.status')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                  />
                  <SortHeader
                    field="createdAt"
                    label={t('admin.operator.merchants.created')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                    firstDescending
                  />
                </tr>
              </thead>
              <tbody>
                {data.items.map((store) => {
                  const name = localizedName(store.name, locale, store.slug);
                  const href = `/stores/${store.storeId}`;
                  const deadline = storeDeadline(store);
                  return (
                    <tr key={store.storeId} {...rowLink(href)}>
                      <td>
                        <Link className="admin-cell-link" to={href} dir="auto">
                          {name}
                        </Link>
                      </td>
                      <td className="admin-code mrigl-ltr">{store.slug}</td>
                      <td>
                        <div>{store.planCode ?? t('admin.operator.noPlan')}</div>
                        {store.planPrice === null || store.periodMonths === null ? null : (
                          <div className="mrigl-muted">
                            {t('admin.billing.price', {
                              amount: formatPrice(store.planPrice.minor),
                              months: store.periodMonths,
                            })}
                          </div>
                        )}
                      </td>
                      <td className="admin-nowrap">
                        {store.lastPayment === null
                          ? t('admin.operator.noPayment')
                          : `${formatPrice(store.lastPayment.amount.minor)} · ${formatDay(locale, store.lastPayment.receivedAt)}`}
                      </td>
                      <td className="admin-nowrap">
                        {store.subscription.paidUntil === null
                          ? '—'
                          : formatDay(locale, store.subscription.paidUntil)}
                      </td>
                      <td className="admin-nowrap">
                        {deadline === null ? '—' : formatDay(locale, deadline)}
                      </td>
                      <td>
                        <SubscriptionBadge state={store.subscription.state} />
                      </td>
                      <td className="admin-nowrap">{formatDay(locale, store.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </GridBody>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={grid.setPage}
            onPageSize={grid.setPageSize}
          />
        </>
      )}
    </div>
  );
}

/** One store: plan, state, usage, payments (record one) and mrigl's invoices. */
export function OperatorStorePage() {
  const { storeId = '' } = useParams();
  const { t, locale, formatPrice, formatDateTime } = useI18n();
  const [detail, setDetail] = useState<StoreDetail | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planId, setPlanId] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void Promise.all([
      unwrap(api.GET('/operator-api/v1/stores/{storeId}', { params: { path: { storeId } } })),
      unwrap(api.GET('/operator-api/v1/plans')),
    ])
      .then(([store, list]) => {
        setDetail(store);
        setPlans(list.items.filter((plan) => plan.active));
        setPlanId(store.planId ?? list.items.find((plan) => plan.active)?.planId ?? '');
      })
      .catch((failure: unknown) => {
        setError(errorMessage(t, failure));
      });
  }, [storeId, t]);

  async function assign() {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      setDetail(
        await unwrap(
          api.PUT('/operator-api/v1/stores/{storeId}/plan', {
            params: { path: { storeId } },
            body: { planId },
          }),
        ),
      );
      setNotice(t('admin.operator.assigned'));
    } catch (failure) {
      setError(operatorError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  if (detail === null) return error === null ? <Loading /> : <Alert tone="error">{error}</Alert>;
  const storeName = localizedName(detail.name, locale, detail.slug);
  const warning = detail.status.state === 'grace' || detail.status.state === 'restricted';
  return (
    <div className="mrigl-stack">
      <p>
        <Link className="admin-back" to="/">
          {t('admin.operator.stores')}
        </Link>
      </p>
      <div className="admin-pagehead">
        <h1 className="mrigl-heading" dir="auto">
          {storeName}
        </h1>
        <div className="admin-pagehead__meta">
          <span className="admin-code mrigl-ltr">{detail.slug}</span>
          <SubscriptionBadge state={detail.status.state} />
        </div>
      </div>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {warning ? (
        <Alert tone="error">{statusText(t, formatDateTime, detail.status)}</Alert>
      ) : (
        <p className="mrigl-muted">{statusText(t, formatDateTime, detail.status)}</p>
      )}
      <OperatorStoreMerchantCard storeId={storeId} />
      <div className="admin-split">
        <Card labelledBy="operator-plan">
          <h2 id="operator-plan" className="mrigl-subheading">
            {detail.plan === null
              ? t('admin.billing.none')
              : t('admin.billing.plan', {
                  name: planName(detail.plan.name, locale, detail.plan.code),
                })}
          </h2>
          {detail.plan === null || detail.plan.price.minor === '0' ? null : (
            <p className="mrigl-muted">
              {t('admin.billing.price', {
                amount: formatPrice(detail.plan.price.minor),
                months: detail.plan.periodMonths,
              })}
            </p>
          )}
          <SelectField
            label={t('admin.operator.plans')}
            value={planId}
            onChange={(event) => {
              setPlanId(event.target.value);
            }}
            options={plans.map((plan) => ({
              value: plan.planId,
              label: `${planName(plan.name, locale, plan.code)} — ${formatPrice(plan.price.minor)}`,
            }))}
          />
          <Button busy={busy} disabled={planId === ''} onClick={() => void assign()}>
            {t('admin.operator.assign')}
          </Button>
        </Card>
        {detail.plan === null ? null : (
          <PaymentForm
            storeId={storeId}
            onDone={(next) => {
              setDetail(next);
              setError(null);
              setNotice(t('admin.operator.paid'));
            }}
            onError={(message) => {
              setNotice(null);
              setError(message);
            }}
          />
        )}
      </div>
      {detail.usage.length === 0 ? null : (
        <section className="mrigl-stack" aria-labelledby="operator-usage">
          <h2 id="operator-usage" className="mrigl-subheading">
            {t('admin.billing.usage')}
          </h2>
          <dl className="admin-stats">
            {detail.usage.map((item) => (
              <div key={item.kind}>
                <dt>{t(`admin.billing.kind.${item.kind}`)}</dt>
                <dd>
                  {item.limit === null
                    ? String(item.used)
                    : `${String(item.used)} / ${String(item.limit)}`}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      <Card labelledBy="operator-payments">
        <h2 id="operator-payments" className="mrigl-subheading">
          {t('admin.operator.payments')}
        </h2>
        {detail.payments.length === 0 && detail.invoices.length === 0 ? (
          <>
            <p className="mrigl-muted">{t('admin.operator.noPayments')}</p>
            <p className="mrigl-muted">{t('admin.billing.noInvoices')}</p>
          </>
        ) : (
          <div className="admin-table-wrap">
            <table className="mrigl-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.order.column.date')}</th>
                  <th scope="col">{t('admin.operator.plan.period')}</th>
                  <th scope="col" className="admin-num">
                    {t('admin.order.column.total')}
                  </th>
                  <th scope="col">{t('admin.operator.method')}</th>
                  <th scope="col">{t('admin.order.invoice')}</th>
                </tr>
              </thead>
              <tbody>
                {paymentLedger(detail.payments, detail.invoices).map(({ payment, invoice }) => {
                  const from = payment?.periodStart ?? invoice?.periodFrom;
                  const to = payment?.periodEnd ?? invoice?.periodTo;
                  const amount = payment?.amount.minor ?? invoice?.total.minor ?? '0';
                  return (
                    <tr key={payment?.paymentId ?? invoice?.invoiceId}>
                      <td className="admin-nowrap">
                        {formatDateTime(payment?.receivedAt ?? invoice?.issuedAt ?? '')}
                      </td>
                      <td>
                        {from === undefined || to === undefined
                          ? '—'
                          : `${formatDay(locale, from)} – ${formatDay(locale, to)}`}
                      </td>
                      <td className="admin-num">{formatPrice(amount)}</td>
                      <td>
                        {payment === null ? (
                          '—'
                        ) : (
                          <>
                            {t(`admin.operator.method.${payment.method}`)}
                            {payment.reference === null ? null : (
                              <span className="admin-cell-sub admin-code mrigl-ltr">
                                {payment.reference}
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        {invoice === null ? (
                          '—'
                        ) : (
                          <a
                            className="admin-invoice-link"
                            href={`/operator-api/v1/stores/${storeId}/invoices/${invoice.invoiceId}/pdf`}
                            download
                            aria-label={t('admin.billing.invoice', {
                              number: invoice.displayNumber,
                              date: formatDateTime(invoice.issuedAt),
                              amount: formatPrice(invoice.total.minor),
                            })}
                          >
                            <span className="admin-code mrigl-ltr">{invoice.displayNumber}</span>
                            <span className="admin-invoice-link__action">
                              {t('admin.download')}
                            </span>
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function PaymentForm({
  storeId,
  onDone,
  onError,
}: {
  readonly storeId: string;
  readonly onDone: (detail: StoreDetail) => void;
  readonly onError: (message: string) => void;
}) {
  const { t } = useI18n();
  const [periods, setPeriods] = useState('1');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<'bank_transfer' | 'cash' | 'other'>('bank_transfer');
  const [reference, setReference] = useState('');
  const [date, setDate] = useState(
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Tunis' }).format(new Date()),
  );
  const [language, setLanguage] = useState<'ar' | 'fr'>('fr');
  const [busy, setBusy] = useState(false);

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const minor = amount.trim() === '' ? undefined : dinarsToMinor(amount);
    if (minor === null) {
      onError(t('admin.operator.plan.error'));
      return;
    }
    setBusy(true);
    try {
      onDone(
        await unwrap(
          api.POST('/operator-api/v1/stores/{storeId}/payments', {
            params: { path: { storeId } },
            body: {
              periods: Number(periods),
              ...(minor === undefined ? {} : { amount: { minor, currency: 'TND' } }),
              method,
              reference: reference.trim() === '' ? null : reference.trim(),
              receivedAt: new Date(`${date}T12:00:00+01:00`).toISOString(),
              locale: language,
            },
          }),
        ),
      );
      setAmount('');
      setReference('');
    } catch (failure) {
      onError(operatorError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-card mrigl-stack"
      aria-label={t('admin.operator.payment')}
      onSubmit={(event) => void save(event)}
    >
      <h2 className="mrigl-subheading">{t('admin.operator.payment')}</h2>
      <div className="admin-form-grid">
        <TextField
          type="number"
          min={1}
          max={24}
          step={1}
          required
          label={t('admin.operator.periods')}
          value={periods}
          onChange={(event) => {
            setPeriods(event.target.value);
          }}
        />
        <TextField
          label={t('admin.operator.amount')}
          inputMode="decimal"
          dir="ltr"
          value={amount}
          onChange={(event) => {
            setAmount(event.target.value);
          }}
        />
        <SelectField
          label={t('admin.operator.method')}
          value={method}
          onChange={(event) => {
            setMethod(event.target.value as typeof method);
          }}
          options={(['bank_transfer', 'cash', 'other'] as const).map((value) => ({
            value,
            label: t(`admin.operator.method.${value}`),
          }))}
        />
        <TextField
          label={t('admin.operator.reference')}
          maxLength={120}
          dir="ltr"
          value={reference}
          onChange={(event) => {
            setReference(event.target.value);
          }}
        />
        <TextField
          type="date"
          required
          label={t('admin.operator.receivedAt')}
          value={date}
          onChange={(event) => {
            setDate(event.target.value);
          }}
        />
        <SelectField
          label={t('admin.operator.invoiceLanguage')}
          value={language}
          onChange={(event) => {
            setLanguage(event.target.value as 'ar' | 'fr');
          }}
          options={(['fr', 'ar'] as const).map((value) => ({
            value,
            label: t(`admin.language.${value}`),
          }))}
        />
      </div>
      <div className="admin-form-actions">
        <Button type="submit" busy={busy}>
          {t('admin.operator.payment')}
        </Button>
      </div>
    </form>
  );
}

/** Plans catalog: open a row for the detail page, or create a new plan. */
export function OperatorPlansPage() {
  const { t, locale, formatPrice } = useI18n();
  const rowLink = useRowLink();
  const location = useLocation();
  const [plans, setPlans] = useState<PlanListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const notice =
    (location.state as { notice?: string } | null)?.notice === 'deleted'
      ? t('admin.operator.plan.deleted')
      : null;

  const load = useCallback(async () => {
    setPlans((await unwrap(api.GET('/operator-api/v1/plans'))).items);
  }, []);

  useEffect(() => {
    load().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [load, t]);

  return (
    <div className="mrigl-stack">
      <div className="admin-pagehead">
        <h1 className="mrigl-heading">{t('admin.operator.plans')}</h1>
        <Link className="mrigl-button mrigl-button--primary" to="/plans/new">
          {t('admin.operator.plan.new')}
        </Link>
      </div>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {plans === null ? (
        <Loading />
      ) : plans.length === 0 ? (
        <p className="mrigl-muted">{t('admin.operator.plan.empty')}</p>
      ) : (
        <div className="admin-grid">
          <div className="admin-table-wrap">
            <table className="mrigl-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.operator.plan.code')}</th>
                  <th scope="col" className="admin-num">
                    {t('admin.operator.plan.price')}
                  </th>
                  <th scope="col">{t('admin.operator.plan.period')}</th>
                  <th scope="col" className="admin-num">
                    {t('admin.operator.plan.subscribers')}
                  </th>
                  <th scope="col">{t('admin.order.status')}</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => {
                  const name = planName(plan.name, locale, plan.code);
                  return (
                    <tr key={plan.planId} {...rowLink(`/plans/${plan.planId}`)}>
                      <td>
                        <Link className="admin-cell-link" to={`/plans/${plan.planId}`}>
                          <span className="admin-code mrigl-ltr">{plan.code}</span>
                        </Link>
                        <div className="mrigl-muted" dir="auto">
                          {name}
                        </div>
                      </td>
                      <td className="admin-num">{formatPrice(plan.price.minor)}</td>
                      <td>{t('admin.operator.plan.months', { count: plan.periodMonths })}</td>
                      <td className="admin-num">
                        {t('admin.operator.plan.subscriberCount', { count: plan.subscriberCount })}
                      </td>
                      <td>
                        {plan.isDefault ? (
                          <span className="admin-status admin-status--sub-active">
                            {t('admin.operator.set.defaultPlan')}
                          </span>
                        ) : null}{' '}
                        {plan.active ? (
                          <span className="admin-status admin-status--sub-active">
                            {t('admin.operator.plan.active')}
                          </span>
                        ) : null}{' '}
                        {plan.offered ? (
                          <span className="admin-status admin-status--sub-trialing">
                            {t('admin.operator.plan.offered')}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** Create a plan, then open its detail page. */
export function OperatorPlanNewPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <div className="mrigl-stack">
      <p>
        <Link className="admin-back" to="/plans">
          {t('admin.operator.plans')}
        </Link>
      </p>
      <div className="admin-pagehead">
        <h1 className="mrigl-heading">{t('admin.operator.plan.new')}</h1>
      </div>
      <PlanForm
        plan={null}
        onSaved={(plan) => {
          void navigate(`/plans/${plan.planId}`, { state: { saved: true } });
          return Promise.resolve();
        }}
      />
    </div>
  );
}

/** One plan: subscribed stores, the editor, and deletion when nothing still depends on it. */
export function OperatorPlanDetailPage() {
  const { planId = '' } = useParams();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [detail, setDetail] = useState<PlanDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(
    (location.state as { saved?: boolean } | null)?.saved === true
      ? t('admin.operator.plan.saved')
      : null,
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setDetail(
      await unwrap(api.GET('/operator-api/v1/plans/{planId}', { params: { path: { planId } } })),
    );
  }, [planId]);

  useEffect(() => {
    load().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [load, t]);

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await unwrap(api.DELETE('/operator-api/v1/plans/{planId}', { params: { path: { planId } } }));
      void navigate('/plans', { state: { notice: 'deleted' } });
    } catch (failure) {
      setError(operatorError(t, failure));
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  if (detail === null) return error === null ? <Loading /> : <Alert tone="error">{error}</Alert>;
  const title = planName(detail.name, locale, detail.code);
  return (
    <div className="mrigl-stack">
      <p>
        <Link className="admin-back" to="/plans">
          {t('admin.operator.plans')}
        </Link>
      </p>
      <div className="admin-pagehead">
        <h1 className="mrigl-heading" dir="auto">
          {title}
        </h1>
        <div className="admin-pagehead__meta">
          <span className="admin-code mrigl-ltr">{detail.code}</span>
          {detail.isDefault ? (
            <span className="admin-status admin-status--sub-active">
              {t('admin.operator.set.defaultPlan')}
            </span>
          ) : null}
          {detail.active ? (
            <span className="admin-status admin-status--sub-active">
              {t('admin.operator.plan.active')}
            </span>
          ) : null}
          {detail.offered ? (
            <span className="admin-status admin-status--sub-trialing">
              {t('admin.operator.plan.offered')}
            </span>
          ) : null}
        </div>
      </div>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <dl className="admin-stats">
        <div>
          <dt>{t('admin.operator.plan.subscribers')}</dt>
          <dd>{t('admin.operator.plan.subscriberCount', { count: detail.subscriberCount })}</dd>
        </div>
        <div>
          <dt>{t('admin.operator.payments')}</dt>
          <dd>{String(detail.paymentCount)}</dd>
        </div>
      </dl>
      <section className="mrigl-stack" aria-labelledby="plan-subscribers">
        <h2 id="plan-subscribers" className="mrigl-subheading">
          {t('admin.operator.plan.subscribers')}
        </h2>
        {detail.subscribers.length === 0 ? (
          <p className="mrigl-muted">{t('admin.operator.noStores')}</p>
        ) : (
          <div className="admin-grid">
            <div className="admin-table-wrap">
              <table className="mrigl-table">
                <thead>
                  <tr>
                    <th scope="col">{t('admin.operator.feedback.store')}</th>
                    <th scope="col">{t('admin.store.slug')}</th>
                    <th scope="col">{t('admin.order.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.subscribers.map((store) => {
                    const name = localizedName(store.name, locale, store.slug);
                    return (
                      <tr key={store.storeId}>
                        <td>
                          <Link
                            className="admin-cell-link"
                            to={`/stores/${store.storeId}`}
                            dir="auto"
                          >
                            {name}
                          </Link>
                        </td>
                        <td className="admin-code mrigl-ltr">{store.slug}</td>
                        <td>
                          <SubscriptionBadge state={store.subscription.state} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
      <PlanForm
        key={`${detail.planId}-${String(detail.version)}`}
        plan={detail}
        onSaved={async (plan) => {
          setNotice(t('admin.operator.plan.saved'));
          setDetail({ ...detail, ...plan });
          await load();
        }}
      />
      <div className="admin-actions">
        {detail.canDelete ? (
          confirmDelete ? (
            <>
              <Button variant="danger" busy={busy} onClick={() => void remove()}>
                {t('admin.operator.plan.confirmDelete')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setConfirmDelete(false);
                }}
              >
                {t('admin.media.cancel')}
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmDelete(true);
              }}
            >
              {t('admin.operator.plan.delete')}
            </Button>
          )
        ) : (
          <div className="mrigl-stack">
            {detail.blockers.map((blocker) => (
              <p key={blocker} className="mrigl-muted">
                {t(
                  blocker === 'subscribers'
                    ? 'admin.operator.plan.cannotDelete.subscribers'
                    : blocker === 'payments'
                      ? 'admin.operator.plan.cannotDelete.payments'
                      : 'admin.operator.plan.cannotDelete.default',
                )}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PlanForm({
  plan,
  onSaved,
}: {
  readonly plan: Plan | null;
  readonly onSaved: (plan: Plan) => Promise<void>;
}) {
  const { t } = useI18n();
  const [code, setCode] = useState(plan?.code ?? '');
  const [names, setNames] = useState<Partial<Record<Locale, string>>>(plan?.name ?? {});
  const [price, setPrice] = useState(plan === null ? '' : minorToDinars(plan.price.minor));
  const [period, setPeriod] = useState(String(plan?.periodMonths ?? 1));
  const [trial, setTrial] = useState(String(plan?.trialDays ?? 0));
  const [grace, setGrace] = useState(String(plan?.graceDays ?? 0));
  const [maxProducts, setMaxProducts] = useState(String(plan?.maxProducts ?? ''));
  const [maxStaff, setMaxStaff] = useState(String(plan?.maxStaff ?? ''));
  const [maxDomains, setMaxDomains] = useState(String(plan?.maxCustomDomains ?? ''));
  const [active, setActive] = useState(plan?.active ?? true);
  const [offered, setOffered] = useState(plan?.offered ?? false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const label =
    plan === null
      ? t('admin.operator.plan.new')
      : t('admin.operator.plan.edit', { code: plan.code });

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const minor = dinarsToMinor(price);
    if (minor === null) {
      setError(t('admin.operator.plan.error'));
      return;
    }
    const body = {
      name: Object.fromEntries(Object.entries(names).filter(([, value]) => value.trim() !== '')),
      price: { minor, currency: 'TND' as const },
      periodMonths: Number(period) as 1 | 3 | 6 | 12,
      trialDays: Number(trial),
      graceDays: Number(grace),
      maxProducts: optionalInt(maxProducts),
      maxStaff: optionalInt(maxStaff),
      maxCustomDomains: optionalInt(maxDomains),
      active,
      offered,
    };
    setBusy(true);
    try {
      if (plan === null) {
        await onSaved(
          await unwrap(
            api.POST('/operator-api/v1/plans', { body: { ...body, code: code.trim() } }),
          ),
        );
      } else {
        await onSaved(
          await unwrap(
            api.PUT('/operator-api/v1/plans/{planId}', {
              params: { path: { planId: plan.planId } },
              body: { ...body, version: plan.version },
            }),
          ),
        );
      }
    } catch (failure) {
      setError(operatorError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-card mrigl-stack"
      aria-label={label}
      onSubmit={(event) => void save(event)}
    >
      <div className="admin-title-row">
        <h2 className="mrigl-subheading">{label}</h2>
        {plan === null ? null : (
          <div className="admin-pagehead__meta">
            <span
              className={`admin-status ${plan.active ? 'admin-status--sub-active' : 'admin-status--sub-none'}`}
            >
              {t('admin.operator.plan.active')}
            </span>
            {plan.offered ? (
              <span className="admin-status admin-status--sub-trialing">
                {t('admin.operator.plan.offered')}
              </span>
            ) : null}
          </div>
        )}
      </div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {plan === null ? (
        <TextField
          label={t('admin.operator.plan.code')}
          required
          dir="ltr"
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
          }}
        />
      ) : null}
      <div className="admin-form-grid">
        {LOCALES.map((language) => (
          <TextField
            key={language}
            label={t('admin.operator.plan.nameIn', { language: t(`admin.language.${language}`) })}
            lang={language}
            dir="auto"
            maxLength={80}
            value={names[language] ?? ''}
            onChange={(event) => {
              setNames({ ...names, [language]: event.target.value });
            }}
          />
        ))}
      </div>
      <div className="admin-form-grid">
        <TextField
          label={t('admin.operator.plan.price')}
          inputMode="decimal"
          dir="ltr"
          required
          value={price}
          onChange={(event) => {
            setPrice(event.target.value);
          }}
        />
        <SelectField
          label={t('admin.operator.plan.period')}
          value={period}
          onChange={(event) => {
            setPeriod(event.target.value);
          }}
          options={[1, 3, 6, 12].map((months) => ({
            value: String(months),
            label: t('admin.operator.plan.months', { count: months }),
          }))}
        />
        <TextField
          type="number"
          min={0}
          max={365}
          label={t('admin.operator.plan.trialDays')}
          value={trial}
          onChange={(event) => {
            setTrial(event.target.value);
          }}
        />
        <TextField
          type="number"
          min={0}
          max={90}
          label={t('admin.operator.plan.graceDays')}
          value={grace}
          onChange={(event) => {
            setGrace(event.target.value);
          }}
        />
      </div>
      <div className="admin-form-grid admin-form-grid--3">
        <TextField
          type="number"
          min={0}
          label={t('admin.operator.plan.maxProducts')}
          value={maxProducts}
          onChange={(event) => {
            setMaxProducts(event.target.value);
          }}
        />
        <TextField
          type="number"
          min={1}
          label={t('admin.operator.plan.maxStaff')}
          value={maxStaff}
          onChange={(event) => {
            setMaxStaff(event.target.value);
          }}
        />
        <TextField
          type="number"
          min={0}
          label={t('admin.operator.plan.maxDomains')}
          value={maxDomains}
          onChange={(event) => {
            setMaxDomains(event.target.value);
          }}
        />
      </div>
      <div className="admin-form-grid">
        <CheckboxGroup
          legend={t('admin.operator.plan.active')}
          options={[{ value: 'active', label: t('admin.operator.plan.active') }]}
          values={active ? ['active'] : []}
          onChange={(values) => {
            setActive(values.includes('active'));
          }}
        />
        <CheckboxGroup
          legend={t('admin.operator.plan.offered')}
          options={[{ value: 'offered', label: t('admin.operator.plan.offeredHint') }]}
          values={offered ? ['offered'] : []}
          onChange={(values) => {
            setOffered(values.includes('offered'));
          }}
        />
      </div>
      <div className="admin-form-actions">
        <Button type="submit" busy={busy}>
          {plan === null ? t('admin.operator.plan.create') : t('admin.save')}
        </Button>
      </div>
    </form>
  );
}

/** mrigl's own invoice details, VAT settings and the default plan for new stores. */
export function OperatorSettingsPage() {
  const { t, locale } = useI18n();
  const [state, setState] = useState<SettingsState | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Kept here: saving remounts the form (new version).
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([
      unwrap(api.GET('/operator-api/v1/settings')),
      unwrap(api.GET('/operator-api/v1/plans')),
    ])
      .then(([settings, list]) => {
        setState(settings);
        setPlans(list.items.filter((plan) => plan.active));
      })
      .catch((failure: unknown) => {
        setError(errorMessage(t, failure));
      });
  }, [t]);

  if (state === null) return error === null ? <Loading /> : <Alert tone="error">{error}</Alert>;
  return (
    <div className="mrigl-stack">
      <div className="admin-pagehead">
        <h1 className="mrigl-heading">{t('admin.operator.settings')}</h1>
      </div>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      <SettingsForm
        key={state.version}
        state={state}
        plans={plans}
        locale={locale}
        onSaved={(next) => {
          setState(next);
          setNotice(t('admin.operator.set.saved'));
        }}
      />
    </div>
  );
}

function SettingsForm({
  state,
  plans,
  locale,
  onSaved,
}: {
  readonly state: SettingsState;
  readonly plans: readonly Plan[];
  readonly locale: Locale;
  readonly onSaved: (state: SettingsState) => void;
}) {
  const { t } = useI18n();
  const initial = state.settings;
  const [legalName, setLegalName] = useState<Partial<Record<Locale, string>>>(initial.legalName);
  const [address, setAddress] = useState<Partial<Record<Locale, string>>>(initial.address);
  const [footer, setFooter] = useState<Partial<Record<Locale, string>>>(initial.footer);
  const [taxId, setTaxId] = useState(initial.taxId ?? '');
  const [prefix, setPrefix] = useState(initial.numberPrefix);
  const [vat, setVat] = useState(initial.vatRateBp === null ? '' : String(initial.vatRateBp / 100));
  const [includes, setIncludes] = useState(initial.pricesIncludeVat);
  const [instructions, setInstructions] = useState<Partial<Record<Locale, string>>>(
    initial.paymentInstructions,
  );
  const [required, setRequired] = useState(initial.onboarding);
  const [defaultPlan, setDefaultPlan] = useState(state.defaultPlanId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const languages = ['fr', 'ar'] as const;

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const clean = (value: Partial<Record<Locale, string>>) =>
      Object.fromEntries(Object.entries(value).filter(([, item]) => item.trim() !== ''));
    setBusy(true);
    try {
      onSaved(
        await unwrap(
          api.PUT('/operator-api/v1/settings', {
            body: {
              version: state.version,
              defaultPlanId: defaultPlan === '' ? null : defaultPlan,
              settings: {
                schemaVersion: 1,
                legalName: clean(legalName),
                address: clean(address),
                footer: clean(footer),
                taxId: taxId.trim() === '' ? null : taxId.trim(),
                numberPrefix: prefix.trim(),
                vatRateBp:
                  vat.trim() === '' ? null : Math.round(Number(vat.replace(',', '.')) * 100),
                pricesIncludeVat: includes,
                paymentInstructions: clean(instructions),
                onboarding: required,
              },
            },
          }),
        ),
      );
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-card mrigl-stack"
      aria-label={t('admin.operator.settings')}
      onSubmit={(event) => void save(event)}
    >
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <div className="admin-fieldset">
        <div className="admin-form-grid">
          {languages.map((language) => (
            <TextField
              key={`name-${language}`}
              label={t('admin.operator.set.legalNameIn', {
                language: t(`admin.language.${language}`),
              })}
              lang={language}
              dir="auto"
              maxLength={200}
              value={legalName[language] ?? ''}
              onChange={(event) => {
                setLegalName({ ...legalName, [language]: event.target.value });
              }}
            />
          ))}
          {languages.map((language) => (
            <TextAreaField
              key={`address-${language}`}
              label={t('admin.operator.set.addressIn', {
                language: t(`admin.language.${language}`),
              })}
              lang={language}
              dir="auto"
              maxLength={500}
              value={address[language] ?? ''}
              onChange={(event) => {
                setAddress({ ...address, [language]: event.target.value });
              }}
            />
          ))}
        </div>
      </div>
      <div className="admin-fieldset">
        <div className="admin-form-grid">
          <TextField
            label={t('admin.operator.set.taxId')}
            dir="ltr"
            maxLength={40}
            value={taxId}
            onChange={(event) => {
              setTaxId(event.target.value);
            }}
          />
          <TextField
            label={t('admin.operator.set.prefix')}
            dir="ltr"
            maxLength={12}
            value={prefix}
            onChange={(event) => {
              setPrefix(event.target.value);
            }}
          />
          <TextField
            label={t('admin.operator.set.vatRate')}
            inputMode="decimal"
            dir="ltr"
            value={vat}
            onChange={(event) => {
              setVat(event.target.value);
            }}
          />
          <CheckboxGroup
            legend={t('admin.operator.set.pricesIncludeVat')}
            options={[{ value: 'yes', label: t('admin.operator.set.pricesIncludeVat') }]}
            values={includes ? ['yes'] : []}
            onChange={(values) => {
              setIncludes(values.includes('yes'));
            }}
          />
        </div>
      </div>
      <div className="admin-fieldset">
        <div className="admin-form-grid">
          {languages.map((language) => (
            <TextAreaField
              key={`footer-${language}`}
              label={t('admin.operator.set.footerIn', {
                language: t(`admin.language.${language}`),
              })}
              lang={language}
              dir="auto"
              maxLength={1000}
              value={footer[language] ?? ''}
              onChange={(event) => {
                setFooter({ ...footer, [language]: event.target.value });
              }}
            />
          ))}
          {languages.map((language) => (
            <TextAreaField
              key={`pay-${language}`}
              label={t('admin.operator.set.paymentInstructionsIn', {
                language: t(`admin.language.${language}`),
              })}
              hint={t('admin.operator.set.paymentInstructionsHint')}
              lang={language}
              dir="auto"
              maxLength={2000}
              value={instructions[language] ?? ''}
              onChange={(event) => {
                setInstructions({ ...instructions, [language]: event.target.value });
              }}
            />
          ))}
        </div>
      </div>
      <div className="admin-fieldset">
        <div className="admin-form-grid">
          <div className="admin-form-span">
            <CheckboxGroup
              legend={t('admin.operator.set.requiredSteps')}
              options={[
                { value: 'contact', label: t('admin.setup.step.contact') },
                { value: 'legal', label: t('admin.setup.step.legal') },
                { value: 'delivery', label: t('admin.setup.step.delivery') },
              ]}
              values={(['contact', 'legal', 'delivery'] as const).filter((key) => required[key])}
              onChange={(values) => {
                setRequired({
                  contact: values.includes('contact'),
                  legal: values.includes('legal'),
                  delivery: values.includes('delivery'),
                });
              }}
            />
          </div>
          <SelectField
            label={t('admin.operator.set.defaultPlan')}
            value={defaultPlan}
            onChange={(event) => {
              setDefaultPlan(event.target.value);
            }}
            options={[
              { value: '', label: t('admin.operator.set.noDefault') },
              ...plans.map((plan) => ({
                value: plan.planId,
                label: planName(plan.name, locale, plan.code),
              })),
            ]}
          />
        </div>
      </div>
      <div className="admin-form-actions">
        <Button type="submit" busy={busy}>
          {t('admin.save')}
        </Button>
      </div>
    </form>
  );
}
